"use client";

import React from "react";

const formatPrice = (price: number): string => {
  const priceStr = price.toString();
  const parts = priceStr.split('.');
  const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return integerPart;
};

type ListingPriceProps = {
  locale: string;
  /** Valor de `operation_type_es` / `operation_type_en`, no del facet derivado. */
  agreement_type?: string;
  price_sale?: number;
  price_rent?: number;
  transfer?: boolean;
  transfer_sale?: number;
  transfer_rent?: number;
};

/**
 * Precio de una tarjeta de resultado.
 *
 * En un traspaso manda `transfer_sale` (pago único por el negocio) y el alquiler del
 * local baja a línea secundaria: es un coste que asume quien entra, no la operación.
 * `operation_type_es` se ignora por completo, porque en un traspaso vale "Alquiler" por
 * un artefacto de captura de Witei — ver traspasos-plan.md §0.
 *
 * Los importes ausentes llegan indexados como 0 (Algolia y Mapbox comparten esa
 * convención), así que 0 significa "sin publicar", nunca "gratis".
 */
export const ListingPrice = ({
  locale,
  agreement_type,
  price_sale,
  price_rent,
  transfer,
  transfer_sale,
  transfer_rent,
}: ListingPriceProps) => {
  const isEs = locale === "es";

  if (transfer) {
    const hasSale = Boolean(transfer_sale && transfer_sale > 0);
    const hasRent = Boolean(transfer_rent && transfer_rent > 0);

    return (
      <div>
        <p className="text-lg font-medium leading-tight tabular-nums text-gray-900">
          {hasSale
            ? `${formatPrice(transfer_sale!)} €`
            : isEs ? "Traspaso a consultar" : "Transfer price on request"}
        </p>
        {hasRent && (
          <p className="text-gray-500 text-xs mt-0.5">
            {isEs
              ? `+ ${formatPrice(transfer_rent!)} €/mes de alquiler`
              : `+ ${formatPrice(transfer_rent!)} €/month rent`}
          </p>
        )}
      </div>
    );
  }

  const isSale = isEs ? agreement_type === "Venta" : agreement_type === "Sale";
  const precio = isSale ? price_sale : price_rent;

  return (
    <p className="text-lg font-medium leading-tight tabular-nums text-gray-900">
      {!precio
        ? isEs ? "A consultar" : "On request"
        : isSale
          ? `${formatPrice(precio)} €`
          : (
            <>
              {formatPrice(precio)} €
              <span className="ml-1 text-xs font-normal text-gray-500">{isEs ? "/mes" : "/month"}</span>
            </>
          )}
    </p>
  );
};
